import React from 'react';
import { Box, Typography, Divider, Chip } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

/**
 * PriceSummary - Displays a clean price breakdown at checkout.
 *
 * Props:
 *   planName: string
 *   billingCycle: string
 *   originalAmount: number
 *   discountAmount: number (optional)
 *   finalAmount: number
 *   currency: string (e.g. 'GBP')
 *   currencySymbol: string (e.g. '£')
 *   couponCode: string (optional)
 *   countryCode: string (optional, for flag display)
 */
const PriceSummary = ({
  planName,
  billingCycle,
  originalAmount,
  discountAmount = 0,
  finalAmount,
  currency = 'GBP',
  currencySymbol = '£',
  couponCode,
  discountSource,
  discountLabel,
  discountPercentage,
  countryCode = 'UK'
}) => {
  const cycleLabel = {
    monthly: 'month',
    quarterly: 'quarter',
    yearly: 'year'
  }[billingCycle] || billingCycle;

  const hasDiscount = discountAmount > 0;

  const currencyFlags = {
    GBP: '🇬🇧', USD: '🇺🇸', EUR: '🇪🇺', PKR: '🇵🇰', QAR: '🇶🇦'
  };

  return (
    <Box sx={{
      p: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper'
    }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5, fontSize: '0.7rem' }}>
        Order Summary
      </Typography>

      {/* Plan Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {planName} <Chip label={billingCycle} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {currencySymbol}{parseFloat(originalAmount || 0).toFixed(2)}/{cycleLabel}
        </Typography>
      </Box>

      {/* Discount Row */}
      {hasDiscount && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle fontSize="small" sx={{ color: 'success.main', fontSize: '0.9rem' }} />
            <Typography variant="body2" color="success.dark">
              {discountLabel || (couponCode ? `Coupon ${couponCode}` : 'Discount')}
            </Typography>
          </Box>
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            −{currencySymbol}{parseFloat(discountAmount).toFixed(2)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Total Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1" fontWeight={700}>
          Total Due
        </Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" fontWeight={800} color="secondary.main">
            {currencySymbol}{parseFloat(finalAmount || originalAmount || 0).toFixed(2)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              /{cycleLabel}
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {currencyFlags[currency] || ''} {currency}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceSummary;
